import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { messages } from "../i18n/index.ts";

interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Panelin üst şeridi — maç sonucunu taşır. */
  accent?: "mint" | "flame" | "amber";
  /** Mobilde varsayılan bottom-sheet; maç sonucu her ekranda ortalanabilir. */
  placement?: "bottom" | "center";
}

/** Alttan açılan panel — sonuç ve yardım ekranları bunu kullanır. */
export function Sheet({
  open,
  title,
  onClose,
  children,
  accent = "mint",
  placement = "bottom",
}: SheetProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="sheet-overlay" data-placement={placement} onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ ["--sheet-accent"]: `var(--color-${accent})` } as CSSProperties}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="font-display text-xl font-extrabold tracking-tight">{title}</h2>
          <button
            ref={closeRef}
            type="button"
            className="btn-ghost"
            onClick={onClose}
            aria-label={messages.common.close}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
